import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./VideoTestimonials.scss";
import { impact } from "../data/stats";
import AsOfNote from "../components/AsOfNote";

// ── Add more videos here as they come in ──────────────────────────────────────
const VIDEOS = [
  {
    id: 1,
    youtubeId: "gdl2i8RqsOE",
    title: "Transforming Schools into Model Institutions",
    name: "Vijender Singh Chauhan",
    role: "Interviewer · Educator · Keynote Speaker",
    tag: "Leadership",
  },
  {
    id: 2,
    youtubeId: "gdl2i8RqsOE",
    title: "Building Systems That Last",
    name: "School Principal",
    role: "Government School · Uttar Pradesh",
    tag: "Operations",
  },
  {
    id: 3,
    youtubeId: "gdl2i8RqsOE",
    title: "How Admissions Grew 3× in One Year",
    name: "School Director",
    role: "Private School · Uttarakhand",
    tag: "Growth",
  },
  {
    id: 4,
    youtubeId: "gdl2i8RqsOE",
    title: "Teacher Development That Actually Works",
    name: "Head of Academics",
    role: "CoGrad Cohort School · Rajasthan",
    tag: "Academics",
  },
  {
    id: 5,
    youtubeId: "gdl2i8RqsOE",
    title: "From Struggling to Top-Performing School",
    name: "School Trustee",
    role: "Rural School · Madhya Pradesh",
    tag: "Impact",
  },
  {
    id: 6,
    youtubeId: "gdl2i8RqsOE",
    title: "Digital Systems Changed Everything",
    name: "School Coordinator",
    role: "CoGrad Partner School · Bihar",
    tag: "Technology",
  },
];

const TAG_COLORS = {
  Leadership:  { bg: "#1e3a5f", text: "#60a5fa" },
  Operations:  { bg: "#1a3528", text: "#4ade80" },
  Growth:      { bg: "#3b2a14", text: "#fbbf24" },
  Academics:   { bg: "#2d1b4e", text: "#c084fc" },
  Impact:      { bg: "#3b1414", text: "#f87171" },
  Technology:  { bg: "#0f2b3d", text: "#38bdf8" },
};

function ThumbCard({ video, onClick }) {
  const thumb = `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;
  const tagStyle = TAG_COLORS[video.tag] || TAG_COLORS.Leadership;

  return (
    <motion.div
      className="vt__card"
      onClick={() => onClick(video)}
      whileHover={{ y: -6, scale: 1.02 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="vt__thumb-wrap">
        <img src={thumb} alt={video.title} className="vt__thumb" />
        <div className="vt__play-overlay">
          <div className="vt__play-btn">
            <Play size={22} fill="white" color="white" />
          </div>
        </div>
        <span className="vt__tag" style={{ background: tagStyle.bg, color: tagStyle.text }}>
          {video.tag}
        </span>
      </div>
      <div className="vt__card-body">
        <p className="vt__card-title">{video.title}</p>
        <div className="vt__card-person">
          <span className="vt__card-name">{video.name}</span>
          <span className="vt__card-role">{video.role}</span>
        </div>
      </div>
    </motion.div>
  );
}

function VideoModal({ video, onClose }) {
  return (
    <AnimatePresence>
      {video && (
        <motion.div
          className="vt__modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="vt__modal-box"
            initial={{ scale: 0.88, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 24 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="vt__modal-close" onClick={onClose}>
              <X size={20} />
            </button>
            <div className="vt__modal-player">
              <iframe
                src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="vt__modal-meta">
              <p className="vt__modal-title">{video.title}</p>
              <span className="vt__modal-name">{video.name}</span>
              <span className="vt__modal-role">{video.role}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function VideoTestimonials() {
  const [activeVideo, setActiveVideo] = useState(null);
  const rowRef = useRef(null);

  const scroll = (dir) => {
    if (!rowRef.current) return;
    rowRef.current.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <div className="vt-page">
      <Helmet>
        <title>Video Testimonials | School Incubation Program | CoGrad</title>
        <meta
          name="description"
          content="Hear from school leaders, principals, and educators who have transformed their institutions through CoGrad's School Incubation Program."
        />
      </Helmet>

      <Header />

      {/* ── HERO ── */}
      <section className="vt__hero">
        <div className="vt__hero-glow vt__hero-glow--1" />
        <div className="vt__hero-glow vt__hero-glow--2" />
        <motion.div
          className="vt__hero-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="vt__eyebrow">School Incubation Program</span>
          <h1>
            Real Schools. <span className="vt__gold">Real Stories.</span>
          </h1>
          <p className="vt__hero-sub">
            Hear directly from school leaders who transformed their institutions through
            CoGrad's structured incubation journey.
          </p>
          <div className="vt__hero-stat-row">
            <div className="vt__hero-stat">
              <strong>{impact.schools.display}</strong>
              <span>Schools Reached</span>
            </div>
            <div className="vt__hero-stat-divider" />
            <div className="vt__hero-stat">
              <strong>26+</strong>
              <span>Schools Transformed</span>
            </div>
            <div className="vt__hero-stat-divider" />
            <div className="vt__hero-stat">
              <strong>{impact.students.display}</strong>
              <span>Students Impacted</span>
            </div>
          </div>
          <AsOfNote className="asof--center" />
        </motion.div>
      </section>

      {/* ── VIDEO CAROUSEL ── */}
      <section className="vt__section">
        <div className="vt__section-head">
          <div>
            <h2 className="vt__section-title">
              Voices from the <span className="vt__gold">Ground</span>
            </h2>
            <p className="vt__section-sub">
              School principals, educators, and directors share their transformation journey.
            </p>
          </div>
          <div className="vt__nav-btns">
            <button className="vt__nav-btn" onClick={() => scroll(-1)} aria-label="Scroll left">
              <ChevronLeft size={20} />
            </button>
            <button className="vt__nav-btn" onClick={() => scroll(1)} aria-label="Scroll right">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="vt__track-wrap">
          <div className="vt__track" ref={rowRef}>
            {VIDEOS.map((v, i) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              >
                <ThumbCard video={v} onClick={setActiveVideo} />
              </motion.div>
            ))}
          </div>
        </div>

        {/* scroll hint dots */}
        <div className="vt__scroll-hint">
          <ChevronLeft size={14} opacity={0.4} />
          <span>scroll to explore</span>
          <ChevronRight size={14} opacity={0.4} />
        </div>
      </section>

      {/* ── PROGRAMME CTA ── */}
      <section className="vt__cta">
        <div className="vt__cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="vt__eyebrow">Apply for the 2026 Cohort</span>
            <h2>
              Is your school ready to <span className="vt__gold">transform?</span>
            </h2>
            <p>
              Selection is limited. CoGrad works only with institutions committed to
              long-term growth and real execution.
            </p>
            <div className="vt__cta-btns">
              <a href="/incubation/register" className="vt__btn-primary">
                Apply for Selection
              </a>
              <a href="/incubation" className="vt__btn-ghost">
                Learn About the Program
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}
